'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import { useEffect, useId, useMemo } from 'react';
import Button from '../../components/Button';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { currencyFormat } from '../../utils/helpers/formatters';
import { getEarliestEndingOffer } from '../../utils/helpers/offers';
import { currencyFormatterNoDecimal } from '../../utils/number';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { CampaignItem } from './components/CampaignItem';
import { HowToApply } from './components/HowToApply';
import { ProviderReward } from './components/ProviderReward';
import { RewardTiles } from './components/RewardTiles';
import { SingSaverReward } from './components/SingSaverReward';
import { SingSaverRewardPL } from './components/SingSaverRewardPL';
import {
  firePromotionsExpand,
  firePromotionsRender,
} from './helpers/analytics';
import {
  computeTotalMaxValue,
  getCurrencyPrefix,
} from './helpers/computeRewardData';
import { getColorSchema } from './helpers/getColorSchema';
import { useHeightCollapse } from './hooks/useHeightCollapse';
import { TRANSLATIONS } from './i18n';
import { SingleProductPromotionProps } from './types';

const SITE_NAMES: Record<string, string> = {
  singsaver: 'SingSaver',
  moneyhero: 'MoneyHero',
  money101: 'Money101',
  moneymax: 'MoneyMax',
};

export const SingleProductPromotion: React.FC<SingleProductPromotionProps> = (
  props,
) => {
  const {
    visible,
    featureColor,
    backgroundColor,
    product,
    additionalCampaign,
    howToApply,
    vertical,
    loanSearchParams,
    pushDataLayer,
  } = props;

  const { brand, lang, currencyDisplay } = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, { brand, lang });
  const colorSchema = getColorSchema(featureColor);
  const collapseContentId = useId();

  const { contentRef, isCollapsed, isExpanded, expand, collapseClass } =
    useHeightCollapse();

  const exclusiveOffers = useMemo(
    () => [
      ...(product?.exclusive_offers ?? []),
      ...(product?.bundle_exclusive_offers ?? []),
    ],
    [product?.exclusive_offers, product?.bundle_exclusive_offers],
  );
  const welcomeOffers = useMemo(
    () => product?.welcome_offers ?? [],
    [product?.welcome_offers],
  );
  const hasBrandReward = exclusiveOffers.some(
    (o) => (o.list_of_gifts?.length ?? 0) > 0,
  );

  const hasProviderReward = welcomeOffers.some(
    (o) => (o.list_of_gifts?.length ?? 0) > 0,
  );
  const hasAdditionalCampaign = !!additionalCampaign;
  const isPersonalLoan = vertical === 'PERSONAL_LOANS';

  const { totalMaxValue } = useMemo(
    () => computeTotalMaxValue(exclusiveOffers, welcomeOffers),
    [exclusiveOffers, welcomeOffers],
  );

  const prefix = useMemo(() => {
    const p = getCurrencyPrefix([...exclusiveOffers, ...welcomeOffers]);
    return p ? currencyFormat(p) : currencyDisplay;
  }, [exclusiveOffers, welcomeOffers, currencyDisplay]);

  const formattedTotal = `${prefix}${currencyFormatterNoDecimal.format(totalMaxValue)}`;
  const siteName = SITE_NAMES[brand] ?? 'SingSaver';
  const providerName = product?.provider?.provider_name ?? '';

  const exclusiveOffer = getEarliestEndingOffer(exclusiveOffers);
  const exclusiveOfferTermsLink =
    exclusiveOffer?.tncLink ?? exclusiveOffers.find((o) => o.tncLink)?.tncLink;

  const welcomeOffer = getEarliestEndingOffer(welcomeOffers);
  const welcomeOfferTermsLink =
    welcomeOffer?.tncLink ?? welcomeOffers.find((o) => o.tncLink)?.tncLink;

  // Countdown badge
  const hasCountdownTimer = exclusiveOffers.some(
    (o) => o.includeCountdownTimer,
  );
  const endDate =
    exclusiveOffer?.period?.end_date ?? welcomeOffer?.period?.end_date;
  const badgeText = useMemo(() => {
    if (!endDate) return hasCountdownTimer ? staticText.limitedTime : undefined;
    const end = new Date(endDate);
    const now = new Date();
    const diffMs = end.getTime() - now.getTime();
    if (diffMs <= 0) return staticText.limitedTime;
    const diffHours = diffMs / (1000 * 60 * 60);
    const dayCount = Math.ceil(diffHours / 24);
    const hourCount = Math.ceil(diffHours);
    const timeStr =
      diffHours >= 24
        ? (dayCount === 1 ? staticText.day : staticText.days).replace(
            '{count}',
            String(dayCount),
          )
        : (hourCount === 1 ? staticText.hour : staticText.hours).replace(
            '{count}',
            String(hourCount),
          );
    return staticText.limitedTimeEnds.replace('{time}', timeStr);
  }, [endDate, hasCountdownTimer, staticText]);

  // Item numbering: sequential, hidden when only one item visible
  const visibleItemCount =
    (hasBrandReward ? 1 : 0) +
    (hasProviderReward ? 1 : 0) +
    (hasAdditionalCampaign ? 1 : 0);
  const showNumbers = visibleItemCount > 1;
  let nextNum = 1;
  const ssNumber = hasBrandReward && showNumbers ? nextNum++ : null;
  const providerNumber = hasProviderReward && showNumbers ? nextNum++ : null;
  const additionalCampaignNumber =
    hasAdditionalCampaign && showNumbers ? nextNum : null;

  // Analytics: render
  useEffect(() => {
    if (!visible || !product) return;

    firePromotionsRender(pushDataLayer, {
      productId: product.uid ?? '',
      market: brand,
      hasProviderReward,
      hasItem3: hasAdditionalCampaign,
      totalMaxValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, product]);

  if (!visible || !product) return null;

  const handleExpand = () => {
    expand();
    firePromotionsExpand(pushDataLayer);
  };

  // T&Cs
  const tncLinks: { label: string; url: string }[] = [];
  if (exclusiveOfferTermsLink) {
    tncLinks.push({
      label: `${staticText.brand_reward_guidance}`,
      url: exclusiveOfferTermsLink,
    });
  }

  if (welcomeOfferTermsLink && providerName) {
    tncLinks.push({
      label: `${staticText.provider_reward_guidance.replace(':Provider', providerName)}`,
      url: welcomeOfferTermsLink,
    });
  }

  if (additionalCampaign?.tncName && additionalCampaign?.tncUrl) {
    tncLinks.push({
      label: additionalCampaign.tncName,
      url: additionalCampaign.tncUrl,
    });
  }

  const backgroundStyle =
    backgroundColor === 'Gradient'
      ? { background: colorSchema.gradient }
      : undefined;

  return (
    <section className="py-spacing/padding/section-y" style={backgroundStyle}>
      <div className="mx-auto max-w-[1130px] px-4">
        {/* Two-column layout */}
        <div className="flex flex-col gap-spacing/gap/y lg:flex-row lg:gap-spacing/gap/x">
          {/* Left column: content */}
          <div
            ref={contentRef}
            id={collapseContentId}
            className={`relative flex flex-1 flex-col gap-spacing/gap/y-sm max-w-[636px] ${collapseClass}`}
          >
            {/* Badge + Section title */}
            <div className="flex flex-col gap-spacing/gap/soft-8px">
              {badgeText && (
                <span
                  className={`inline-block self-start rounded-radius/tooltip px-spacing/padding/soft-8px py-spacing/padding/soft-4px font-font-weight/bullet-2 text-font-size/bullet-2 leading-line-height/bullet-2 ${colorSchema.badgeBg} ${colorSchema.badgeText}`}
                >
                  {badgeText}
                </span>
              )}
              <h2 className="font-font-weight/display-1 text-font-size/display-1 leading-line-height/display-1 text-text/primary">
                {`${staticText.sectionTitle} ${formattedTotal}`}
              </h2>
            </div>

            {/* Tablet/Mobile: carousel on top */}
            <div className="lg:hidden">
              <RewardTiles
                exclusiveOffers={exclusiveOffers}
                welcomeOffers={welcomeOffers}
                lang={lang}
                staticText={staticText}
                pushDataLayer={pushDataLayer}
              />
            </div>

            {/* Item 1: SingSaver reward */}
            {isPersonalLoan ? (
              <SingSaverRewardPL
                offers={exclusiveOffers}
                staticText={staticText}
                providerName={providerName}
                itemNumber={ssNumber}
                siteName={siteName}
                bulletColor={colorSchema.bulletColor}
                loanAmount={Number(loanSearchParams?.amount) || 0}
                loanTenure={Number(loanSearchParams?.tenure) || 0}
              />
            ) : (
              <SingSaverReward
                offers={exclusiveOffers}
                staticText={staticText}
                itemNumber={ssNumber}
                bulletColor={colorSchema.bulletColor}
              />
            )}

            {/* Item 2: Provider reward */}
            {hasProviderReward && (
              <ProviderReward
                offers={welcomeOffers}
                providerName={providerName}
                siteName={siteName}
                staticText={staticText}
                itemNumber={providerNumber}
                bulletColor={colorSchema.bulletColor}
              />
            )}

            {/* Item 3: Campaign */}
            {additionalCampaign && (
              <CampaignItem
                additionalCampaign={additionalCampaign}
                staticText={staticText}
                itemNumber={additionalCampaignNumber}
                bulletColor={colorSchema.bulletColor}
              />
            )}

            {/* How to Apply */}
            <HowToApply
              content={howToApply}
              staticText={staticText}
              forceExpanded={isExpanded}
            />

            {/* T&Cs */}
            {tncLinks.length > 0 && (
              <p className="font-font-weight/body-2 text-font-size/body-2 leading-line-height/body-2-relaxed text-text/secondary">
                {staticText.tncApply}&nbsp;
                {tncLinks.map((link, i) => (
                  <span key={link.label}>
                    {i > 0 && i < tncLinks.length - 1 && ', '}
                    {i > 0 &&
                      i === tncLinks.length - 1 &&
                      ` ${staticText.tncAnd} `}
                    <a
                      href={link.url}
                      className="underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.label}
                    </a>
                  </span>
                ))}
                .
              </p>
            )}

            {/* Fade overlay for collapsed state */}
            {isCollapsed && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent"
              />
            )}
          </div>

          {/* Right column: desktop reward tiles grid */}
          <div className="hidden w-[438px] shrink-0 lg:block">
            <RewardTiles
              exclusiveOffers={exclusiveOffers}
              welcomeOffers={welcomeOffers}
              lang={lang}
              staticText={staticText}
              pushDataLayer={pushDataLayer}
            />
          </div>
        </div>

        {/* Collapse expand button (tablet/mobile) */}
        {isCollapsed && (
          <Button
            className="w-full"
            variant="outline"
            styleType="neutral-light-and-blue"
            aria-expanded={false}
            aria-controls={collapseContentId}
            onClick={handleExpand}
          >
            {staticText.showMore}
          </Button>
        )}
      </div>
    </section>
  );
};
